<template>
  <div class="qct-shell">

    <!-- ════════════════════════════════════════
         TOOLBAR
    ════════════════════════════════════════ -->
    <div class="qct-toolbar">

      <!-- Left: title + count -->
      <div class="qct-toolbar-left">
        <div class="qct-title-block">
          <span class="qct-title">{{ title }}</span>
          <span class="qct-badge">{{ filteredData.length }}</span>
        </div>
      </div>

      <!-- Right: controls -->
      <div class="qct-toolbar-right">

        <!-- Date Range Picker -->
        <div class="date-range-pill" v-if="showDateRange">
          <v-menu v-model="datePickerOpen" :close-on-content-click="false" location="bottom center" offset="10">
            <template #activator="{ props: mp }">
              <button class="date-range-btn" v-bind="mp">
                <v-icon size="13" color="#2563eb" style="margin-right:5px">mdi-calendar-range-outline</v-icon>
                <span>{{ dateRangeLabel }}</span>
              </button>
            </template>
            <v-card class="date-picker-card" elevation="8" rounded="lg">
              <v-card-title class="pa-2 text-center" style="font-size: 14px;">Select Date Range</v-card-title>
              <v-divider />
              <v-card-text class="pa-1">
                <div class="calendar-container">
                  <v-date-picker
                    v-model="dateRange"
                    multiple="range"
                    color="primary"
                    hide-header
                    show-adjacent-months
                    elevation="0"
                    @update:model-value="onCalendarDateSelect"
                  />
                </div>
                <div class="selected-dates" v-if="startDate && endDate">
                  <div class="selected-date-item">
                    <span class="date-type">Start:</span>
                    <span class="date-value">{{ formatDisplayDate(startDate) }}</span>
                  </div>
                  <div class="selected-date-item">
                    <span class="date-type">End:</span>
                    <span class="date-value">{{ formatDisplayDate(endDate) }}</span>
                  </div>
                </div>
                <div class="quick-ranges">
                  <div class="quick-ranges-row">
                    <button 
                      v-for="range in quickRanges.slice(0, 3)" 
                      :key="range.label"
                      class="quick-range-btn"
                      @click="setQuickRange(range)"
                    >
                      {{ range.label }}
                    </button>
                  </div>
                  <div class="quick-ranges-row">
                    <button 
                      v-for="range in quickRanges.slice(3, 6)" 
                      :key="range.label"
                      class="quick-range-btn"
                      @click="setQuickRange(range)"
                    >
                      {{ range.label }}
                    </button>
                  </div>
                </div>
              </v-card-text>
              <v-divider />
              <v-card-actions class="px-3 py-2 d-flex justify-space-between">
                <span class="range-display">{{ selectedRangeText }}</span>
                <div>
                  <v-btn size="small" variant="text" @click="clearDates">Clear</v-btn>
                  <v-btn size="small" variant="text" color="primary" @click="datePickerOpen = false">Close</v-btn>
                </div>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>

        <!-- Separator -->
        <div class="qct-sep" />

        <!-- Search -->
        <div class="qct-search-wrap">
          <v-icon size="15" color="#9ca3af" class="qct-search-icon">mdi-magnify</v-icon>
          <input
            v-model="search"
            class="qct-search-input"
            placeholder="Search…"
            type="text"
          />
          <button v-if="search" class="qct-search-clear" @click="search = ''">
            <v-icon size="13">mdi-close</v-icon>
          </button>
        </div>

        <!-- Download -->
        <button class="qct-icon-btn" @click="$emit('download')" title="Download">
          <v-icon size="17" color="#6b7280">mdi-download-outline</v-icon>
        </button>

      </div>
    </div>

    <!-- ════════════════════════════════════════
         TABLE
    ════════════════════════════════════════ -->
    <div class="qct-scroll">
      <table class="qct-table">

        <!-- HEAD -->
        <thead>
          <tr>
            <th
              v-for="(col, colIdx) in columns"
              :key="col.field"
              :style="{ width: colWidths[colIdx] + 'px', minWidth: '60px' }"
              :class="['qct-th', col.sortable !== false ? 'sortable' : '']"
              @click="col.sortable !== false && sortBy(col.field)"
            >
              <div class="qct-th-inner">
                <span>{{ col.header }}</span>
                <span v-if="col.sortable !== false" class="qct-sort-icon">
                  <v-icon size="12" :color="sortField === col.field ? '#2563eb' : '#d1d5db'">
                    {{ sortField === col.field ? (sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-unfold-more-horizontal' }}
                  </v-icon>
                </span>
              </div>
              <!-- Resize handle -->
              <div
                class="qct-resize-handle"
                @mousedown.stop.prevent="startResize($event, colIdx)"
              />
            </th>
            <th class="qct-th qct-th-action">Actions</th>
          </tr>
        </thead>

        <!-- BODY — data -->
        <tbody v-if="!skeleLoading && paginatedData.length > 0">
          <tr
            v-for="(row, idx) in paginatedData"
            :key="row.id || idx"
            class="qct-row"
            @click="$emit('rowClick', row)"
          >
            <td v-for="col in columns" :key="col.field" class="qct-td">

              <template v-if="col.field === 'status'">
                <span :class="['qct-status', getStatusClass(row[col.field])]">
                  <span class="qct-status-dot" />
                  {{ row[col.field] || '—' }}
                </span>
              </template>

              <template v-else-if="col.field === 'severity'">
                <span :class="['qct-severity', getSeverityClass(row[col.field])]">
                  {{ formatLabel(row[col.field]) || '—' }}
                </span>
              </template>

              <template v-else-if="col.field === 'errorCategory' || col.field === 'errorType'">
                <span class="qct-cell" :title="formatLabel(row[col.field])">
                  {{ formatLabel(row[col.field]) || '—' }}
                </span>
              </template>

              <template v-else-if="col.field === 'priority'">
                <span :class="['qct-priority', getPriorityClass(row[col.field])]">
                  {{ row[col.field] || '—' }}
                </span>
              </template>

              <template v-else-if="col.type === 'date' || col.type === 'datetime'">
                <span class="qct-date">{{ formatDate(row[col.field]) }}</span>
              </template>

              <template v-else>
                <span class="qct-cell" :title="String(row[col.field] ?? '')">
                  {{ row[col.field] || '—' }}
                </span>
              </template>

            </td>

            <!-- Action column -->
            <td class="qct-td qct-td-action" @click.stop>
              <div class="qct-actions">
                <template v-if="title === 'QC Error Approval' && accessPrivilege.indexOf('APPROVE') >= 0">
                  <button class="qct-act-btn approve" @click="$emit('approve', row)" title="Approve">
                    <v-icon size="15">mdi-check-circle-outline</v-icon>
                  </button>
                  <button class="qct-act-btn reject" @click="$emit('reject', row)" title="Reject">
                    <v-icon size="15">mdi-close-circle-outline</v-icon>
                  </button>
                </template>
                <template v-if="title === 'QC Errors'">
                  <span v-if="row.request_to_remove === 1" class="qct-status st-Request">
                    <span class="qct-status-dot" />
                    Requested
                  </span>
                  <!-- Show rejected chip for request_to_remove = 2 -->
                  <span v-if="row.request_to_remove === 2" class="qct-status st-rejected">
                    <span class="qct-status-dot" />
                    Rejected
                  </span>
                  <!-- Show edit button only for request_to_remove = 0 or 1 -->
                  <button 
                    v-if="row.request_to_remove !== 1 && row.request_to_remove !== 2" 
                    class="qct-act-btn edit" 
                    @click="$emit('edit', row)" 
                    title="Edit"
                  >
                    <v-icon size="15">mdi-pencil-outline</v-icon>
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- BODY — empty -->
        <tbody v-else-if="!skeleLoading && filteredData.length === 0">
          <tr>
            <td :colspan="columns.length + 1" class="qct-empty">
              <div class="qct-empty-inner">
                <div class="qct-empty-icon-wrap">
                  <v-icon size="32" color="#d1d5db">mdi-database-search-outline</v-icon>
                </div>
                <p class="qct-empty-title">No records found</p>
                <p class="qct-empty-sub">Try adjusting your search or selecting a different week</p>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- BODY — loading skeleton -->
        <tbody v-else>
          <tr v-for="n in 7" :key="n" class="qct-skeleton-row">
            <td v-for="col in columns" :key="col.field" class="qct-td">
              <div class="qct-skeleton" :style="{ width: n % 2 === 0 ? '70%' : '55%' }" />
            </td>
            <td class="qct-td">
              <div class="qct-skeleton" style="width:60px" />
            </td>
          </tr>
        </tbody>

      </table>
    </div>

    <!-- ════════════════════════════════════════
         FOOTER
    ════════════════════════════════════════ -->
    <div class="qct-footer">
      <span class="qct-count">
        {{ filteredData.length === 0 ? 'No records' : `${paginationStart}–${paginationEnd} of ${filteredData.length} records` }}
      </span>

      <div class="qct-pagination">
        <button class="qct-pg-btn" :disabled="currentPage === 1" @click="currentPage--">
          <v-icon size="16">mdi-chevron-left</v-icon>
        </button>

        <template v-for="page in visiblePages" :key="page">
          <button
            :class="['qct-pg-num', page === currentPage ? 'active' : '']"
            @click="currentPage = page"
          >{{ page }}</button>
        </template>

        <button class="qct-pg-btn" :disabled="currentPage === totalPages" @click="currentPage++">
          <v-icon size="16">mdi-chevron-right</v-icon>
        </button>
      </div>

      <div class="qct-page-size">
        <span class="qct-ps-label">Rows</span>
        <select v-model.number="pageSize" class="qct-ps-select" @change="currentPage = 1">
          <option v-for="n in [10, 20, 50, 100]" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
    </div>

  </div>
</template>

<script>
import RolePermissionService from "@/api/service/RolePermission/RolePermissionService";

export default {
  name: 'QcExceptionTable',
  

  props: {
    tableData: { type: Array,   default: () => [] },
    columns:   { type: Array,   default: () => [] },
    skeleLoading:   { type: Boolean, default: false    },
    title:     { type: String,  default: ''       },
    showDateRange: { type: Boolean, default: true }
  },

  emits: ['add', 'download', 'view', 'edit', 'delete', 'rowClick', 'dateChanged', 'approve', 'reject'],

  data() {
    return {
      search:         '',
      statusFilter:   null,
      accessPrivilege: [],
      sortField:      '',
      sortDir:        'asc',
      currentPage:    1,
      pageSize:       10,
      allSelected:    false,
      startDate:      '',
      endDate:        '',
      dateRange:      [],
      datePickerOpen: false,
      quickRanges: [
        { label: 'Today', days: 0 },
        { label: 'Last 7 days', days: 6 },
        { label: 'Last 30 days', days: 29 },
        { label: 'Last 90 days', days: 89 },
        { label: 'This Month', type: 'month' },
        { label: 'Last Month', type: 'lastMonth' }
      ],
      // Column resize state
      colWidths:      [],
      resizing:       { active: false, colIdx: null, startX: 0, startW: 0 },
      statusOptions: [
        { label: 'Open',     value: 'open'     },
        { label: 'Pending',  value: 'pending'  },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Closed',   value: 'closed'   },
      ],
    };
  },

  computed: {
    dateRangeLabel() {
      if (!this.startDate || !this.endDate) return 'Select Date Range';
      return `${this.formatDisplayDate(this.startDate)} – ${this.formatDisplayDate(this.endDate)}`;
    },

    selectedRangeText() {
      if (!this.startDate || !this.endDate) return 'No range selected';
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return `${days} day${days > 1 ? 's' : ''} selected`;
    },

    filteredData() {
      let data = this.tableData.map((row) => ({ ...row }));
      if (this.search) {
        const term = this.search.toLowerCase();
        data = data.filter((row) =>
          Object.values(row).some((val) => String(val ?? '').toLowerCase().includes(term))
        );
      }
      if (this.statusFilter) {
        data = data.filter((row) => String(row.status ?? '').toLowerCase() === this.statusFilter);
      }
      if (this.sortField) {
        data = [...data].sort((a, b) => {
          const va = String(a[this.sortField] ?? '').toLowerCase();
          const vb = String(b[this.sortField] ?? '').toLowerCase();
          if (va < vb) return this.sortDir === 'asc' ? -1 : 1;
          if (va > vb) return this.sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }
      return data;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredData.length / this.pageSize));
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredData.slice(start, start + this.pageSize);
    },

    paginationStart() {
      return this.filteredData.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredData.length);
    },

    visiblePages() {
      const pages = [];
      for (let i = Math.max(1, this.currentPage - 2); i <= Math.min(this.totalPages, this.currentPage + 2); i++) {
        pages.push(i);
      }
      return pages;
    },
  },

  async created() {
    this.accessPrivilege = await RolePermissionService.isAllowed('QCA')
    console.log(this.accessPrivilege ,"this.accessPrivilege ");
    this.initializeDates();
    this.$nextTick(() => this.emitDateChanged());
  },

  mounted() {
    this.initColWidths();
  },

  beforeUnmount() {
    this.stopResize();
  },

  watch: {
    search()       { this.currentPage = 1; },
    statusFilter() { this.currentPage = 1; },
    columns()      { this.initColWidths(); },
  },

  methods: {
    // ── Column resize ──
    initColWidths() {
      this.colWidths = this.columns.map((col) => parseInt(col.width) || 130);
    },

    startResize(e, colIdx) {
      this.resizing = {
        active: true,
        colIdx,
        startX: e.clientX,
        startW: this.colWidths[colIdx],
      };
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup',   this.stopResize);
      document.body.style.cursor     = 'col-resize';
      document.body.style.userSelect = 'none';
    },

    onResize(e) {
      if (!this.resizing.active) return;
      const delta    = e.clientX - this.resizing.startX;
      const newWidth = Math.max(60, this.resizing.startW + delta);
      const widths   = [...this.colWidths];
      widths[this.resizing.colIdx] = newWidth;
      this.colWidths = widths;
    },

    stopResize() {
      this.resizing.active           = false;
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup',   this.stopResize);
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
    },

    // ── Date Range helpers ──
    initializeDates() {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      this.startDate = this.toInputDateString(startOfMonth);
      this.endDate = this.toInputDateString(today);
      // Initialize calendar picker as well
      this.dateRange = [startOfMonth, today];
    },

    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    },

    onCalendarDateSelect(selectedDates) {
      console.log('Calendar selection:', selectedDates);
      if (selectedDates && selectedDates.length >= 2) {
        // Sort dates to ensure start is before end
        const sortedDates = selectedDates.sort((a, b) => new Date(a) - new Date(b));
        this.startDate = this.toInputDateString(new Date(sortedDates[0]));
        this.endDate = this.toInputDateString(new Date(sortedDates[sortedDates.length - 1]));
        this.onDateChange();
      } else if (selectedDates && selectedDates.length === 1) {
        // First click - set start date
        this.startDate = this.toInputDateString(new Date(selectedDates[0]));
        this.endDate = '';
      }
    },

    onDateChange() {
      if (this.startDate && this.endDate) {
        this.emitDateChanged();
      }
    },

    setQuickRange(range) {
      const today = new Date();
      let startDate, endDate;

      if (range.type === 'month') {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      } else if (range.type === 'lastMonth') {
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      } else {
        endDate = new Date(today);
        startDate = new Date(today);
        startDate.setDate(today.getDate() - range.days);
      }

      this.startDate = this.toInputDateString(startDate);
      this.endDate = this.toInputDateString(endDate);
      // Update the calendar picker as well
      this.dateRange = [startDate, endDate];
      this.onDateChange();
    },

    clearDates() {
      this.startDate = '';
      this.endDate = '';
      this.dateRange = [];
    },

    emitDateChanged() {
      this.$emit('dateChanged', {
        start_date: this.startDate,
        end_date: this.endDate
      });
    },

    toInputDateString(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDir   = 'asc';
      }
    },

    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    getStatusClass(s) {
      return { open: 'st-open', pending: 'st-pending', approved: 'st-approved', rejected: 'st-rejected', closed: 'st-closed' }[String(s ?? '').toLowerCase()] || 'st-default';
    },

    getSeverityClass(s) {
      return { critical: 'sv-critical', high: 'sv-high', medium: 'sv-medium', low: 'sv-low' }[String(s ?? '').toLowerCase()] || 'sv-default';
    },

    getPriorityClass(p) {
      return { critical: 'pr-critical', high: 'pr-high', medium: 'pr-medium', low: 'pr-low' }[String(p ?? '').toLowerCase()] || 'pr-default';
    },

    formatLabel(value) {
      if (!value) return '—';
      const words = ['business', 'impacted', 'data', 'incorrect', 'gap', 'high', 'low', 'medium', 'chargeback', 'priority',
        'error', 'severity', 'status', 'type', 'code', 'name', 'date', 'time', 'user',
        'created', 'updated', 'assigned', 'approved', 'rejected', 'pending', 'closed', 'open'];
      let str = String(value)
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/[_.-]+/g, ' ')
        .trim()
        .toLowerCase();
      const regex = new RegExp(`(${words.join('|')})`, 'gi');
      str = str.replace(/\s+/g, ' ').replace(regex, ' $1 ').replace(/\s+/g, ' ').trim();
      return str.replace(/\b\w/g, c => c.toUpperCase());
    }

  },
};
</script>

<style scoped>
/* ─────────────────────────────────────────
   SHELL
───────────────────────────────────────── */
.qct-shell {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04);
  overflow: hidden;
  font-family: 'Inter', 'Roboto', sans-serif;
}

/* ─────────────────────────────────────────
   TOOLBAR
───────────────────────────────────────── */
.qct-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #ffffff;
  border-bottom: 1px solid #f3f4f6;
  gap: 12px;
  flex-wrap: wrap;
}

.qct-toolbar-left { display: flex; align-items: center; gap: 12px; }
.qct-toolbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.qct-title-block { display: flex; align-items: center; gap: 8px; }

.qct-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}

.qct-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 20px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
}

.qct-sep {
  width: 1px;
  height: 22px;
  background: #e5e7eb;
  flex-shrink: 0;
}

/* ─────────────────────────────────────────
   DATE RANGE PICKER
───────────────────────────────────────── */
.date-range-pill {
  display: inline-flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  padding: 6px 12px;
  height: 36px;
}

.date-range-btn {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  font-size: 12.5px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s;
  user-select: none;
}
.date-range-btn:hover { background: #eff6ff; border-radius: 4px; padding: 4px 8px; }

.date-picker-card { 
  width: 320px;
  max-width: 320px;
  overflow-x: hidden;
}

.calendar-container {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  width: 100%;
  overflow: hidden;
}

.calendar-container :deep(.v-date-picker) {
  width: 280px !important;
  max-width: 280px;
}

.selected-dates {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 8px;
  padding: 6px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 12px;
}

.selected-date-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.date-type {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.date-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.quick-ranges {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}

.quick-ranges-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.quick-range-btn {
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all .15s;
  flex: 1;
  min-width: 0;
  text-align: center;
}
.quick-range-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #2563eb;
}

.range-display {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

/* ─────────────────────────────────────────
   WEEK PILL
───────────────────────────────────────── */
.week-pill {
  display: inline-flex;
  align-items: center;
  gap: 0;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  padding: 3px 4px;
  height: 36px;
}

.week-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  transition: background .15s, color .15s;
}
.week-arrow:hover { background: #e5e7eb; color: #111827; }

.week-range {
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s;
  user-select: none;
}
.week-range:hover { background: #eff6ff; }

.week-today {
  margin-left: 3px;
  padding: 0 10px;
  height: 26px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  transition: background .15s;
  white-space: nowrap;
}
.week-today:hover { background: #dbeafe; }

.week-picker-card { min-width: 300px; }
.picker-hint { font-size: 11.5px; color: #6b7280; font-weight: 500; }

/* ─────────────────────────────────────────
   SEARCH
───────────────────────────────────────── */
.qct-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  height: 36px;
  padding: 0 10px;
  gap: 6px;
  transition: border-color .2s, box-shadow .2s;
}
.qct-search-wrap:focus-within {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(37,99,235,.08);
  background: #fff;
}

.qct-search-icon { flex-shrink: 0; }

.qct-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #111827;
  width: 180px;
}
.qct-search-input::placeholder { color: #9ca3af; }

.qct-search-clear {
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ca3af;
  padding: 0;
}
.qct-search-clear:hover { color: #374151; }

/* ─────────────────────────────────────────
   ICON BUTTON
───────────────────────────────────────── */
.qct-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 9px;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.qct-icon-btn:hover { background: #f3f4f6; border-color: #d1d5db; }

/* ─────────────────────────────────────────
   SCROLL CONTAINER
───────────────────────────────────────── */
.qct-scroll {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 62vh;
}

/* ─────────────────────────────────────────
   TABLE
───────────────────────────────────────── */
.qct-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

/* HEAD */
.qct-table thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

.qct-th {
  position: relative;
  padding: 10px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  user-select: none;
  overflow: hidden;
}

.qct-th.sortable { cursor: pointer; }
.qct-th.sortable:hover { background: #f3f4f6; color: #374151; }

.qct-th-inner {
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
}

.qct-sort-icon { display: flex; align-items: center; opacity: .7; flex-shrink: 0; }

/* Resize handle */
.qct-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
  background: transparent;
  transition: background .15s;
}
.qct-resize-handle:hover,
.qct-resize-handle:active {
  background: #2563eb;
  opacity: .5;
}

.qct-th-action {
  text-align: center;
  width: 100px;
}

/* ROWS */
.qct-row {
  cursor: pointer;
  transition: background .12s;
  border-bottom: 1px solid #f3f4f6;
}
.qct-row:hover { background: #f8faff; }
.qct-row:hover .qct-actions { opacity: 1; }
.qct-row:last-child { border-bottom: none; }

/* Zebra stripe */
.qct-row:nth-child(even) { background: #fafafa; }
.qct-row:nth-child(even):hover { background: #f0f5ff; }

.qct-td {
  padding: 10px 16px;
  color: #374151;
  vertical-align: middle;
  max-width: 180px;
}

.qct-td-action {
  text-align: center;
  max-width: 100px;
}

.qct-cell {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
  white-space: nowrap;
  font-size: 13px;
  color: #374151;
}

.qct-date {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

/* ─────────────────────────────────────────
   STATUS BADGES
───────────────────────────────────────── */
.qct-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px 3px 7px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}

.qct-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.st-open     { background: #eff6ff; color: #1d4ed8; }
.st-open     .qct-status-dot { background: #3b82f6; }
.st-Request  { background: #e9ef924d; color:#FF8c00; }
.st-Request  .qct-status-dot { background:#FF8c00; }
.st-approved { background: #f0fdf4; color: #166534; }
.st-approved .qct-status-dot { background: #22c55e; }
.st-rejected { background: #fef2f2; color: #991b1b; }
.st-rejected .qct-status-dot { background: #ef4444; }
.st-closed   { background: #f3f4f6; color: #4b5563; }
.st-closed   .qct-status-dot { background: #9ca3af; }
.st-default  { background: #f3f4f6; color: #6b7280; }
.st-default  .qct-status-dot { background: #d1d5db; }

/* ─────────────────────────────────────────
   SEVERITY BADGES
───────────────────────────────────────── */
.qct-severity {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  
}

.sv-critical { background: #fdf2f8; color: #9d174d; border: 1px solid #fbcfe8; }
.sv-high     { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
.sv-medium   { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
.sv-low      { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
.sv-default  { background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }

/* ─────────────────────────────────────────
   PRIORITY BADGES
───────────────────────────────────────── */
.qct-priority {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}

.pr-critical { background: #fdf2f8; color: #9d174d; }
.pr-high     { background: #fef2f2; color: #b91c1c; }
.pr-medium   { background: #fff7ed; color: #c2410c; }
.pr-low      { background: #f0fdf4; color: #15803d; }
.pr-default  { background: #f3f4f6; color: #6b7280; }

/* ─────────────────────────────────────────
   ACTION BUTTONS
───────────────────────────────────────── */
.qct-actions {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  opacity: 1;
  transition: opacity .15s;
}

.qct-act-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  transition: background .15s, transform .1s;
  background: transparent;
}
.qct-act-btn:hover { transform: scale(1.1); }

.qct-act-btn.view  { color: #2563eb; }
.qct-act-btn.view:hover  { background: #eff6ff; }
.qct-act-btn.edit    { color: #16a34a; }
.qct-act-btn.edit:hover    { background: #f0fdf4; }
.qct-act-btn.del     { color: #dc2626; }
.qct-act-btn.del:hover     { background: #fef2f2; }
.qct-act-btn.approve { color: #16a34a; }
.qct-act-btn.approve:hover { background: #f0fdf4; }
.qct-act-btn.reject  { color: #dc2626; }
.qct-act-btn.reject:hover  { background: #fef2f2; }

/* ─────────────────────────────────────────
   EMPTY STATE
───────────────────────────────────────── */
.qct-empty { padding: 64px 20px; }

.qct-empty-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.qct-empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qct-empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.qct-empty-sub {
  font-size: 12.5px;
  color: #9ca3af;
  margin: 0;
  text-align: center;
}

/* ─────────────────────────────────────────
   SKELETON
───────────────────────────────────────── */
.qct-skeleton-row { border-bottom: 1px solid #f3f4f6; }

.qct-skeleton {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ─────────────────────────────────────────
   FOOTER
───────────────────────────────────────── */
.qct-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 20px;
  border-top: 1px solid #f3f4f6;
  background: #fafafa;
  flex-wrap: wrap;
  gap: 10px;
}

.qct-count {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

/* Pagination */
.qct-pagination {
  display: flex;
  align-items: center;
  gap: 3px;
}

.qct-pg-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 7px;
  cursor: pointer;
  color: #374151;
  transition: background .15s, border-color .15s;
}
.qct-pg-btn:hover:not(:disabled) { background: #f3f4f6; border-color: #d1d5db; }
.qct-pg-btn:disabled { opacity: .35; cursor: default; }

.qct-pg-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 4px;
  border: 1px solid transparent;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: background .15s;
}
.qct-pg-num:hover { background: #f3f4f6; }
.qct-pg-num.active {
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  border-color: #2563eb;
}

/* Page size */
.qct-page-size {
  display: flex;
  align-items: center;
  gap: 7px;
}

.qct-ps-label {
  font-size: 12px;
  color: #6b7280;
}

.qct-ps-select {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #fff;
  font-size: 12.5px;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: border-color .15s;
}
.qct-ps-select:focus { border-color: #93c5fd; }
</style>
